import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { CheckCircle, Mail, Phone, Building2 } from 'lucide-react';
import { API_BASE } from '../apiBase';
import { EMAIL_RE, PHONE_RE, MESSAGES, onlyDigits } from '../validation';

export const ContactForm = ({ isDemo = false, onClose }) => {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    employees: '',
    industry: '',
    message: '',
    website: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'phone' ? onlyDigits(value, 10) : value
    });
    setErrors((p) => ({ ...p, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData.company.trim()) e.company = MESSAGES.required;
    if (!formData.name.trim()) e.name = MESSAGES.required;
    if (!EMAIL_RE.test(formData.email.trim())) e.email = MESSAGES.email;
    if (formData.phone && !PHONE_RE.test(formData.phone)) e.phone = 'El teléfono debe tener 10 dígitos.';
    if (!isDemo && !formData.message.trim()) e.message = MESSAGES.required;
    return e;
  };

  const errCls = (k) => (errors[k] ? 'border-red-500' : '');
  const errMsg = (k) => errors[k] && <p className="text-red-500 text-xs" data-testid={`demo-error-${k}`}>{errors[k]}</p>;

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/api/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, email: formData.email.trim(), is_demo: isDemo })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status !== 'success') {
        if (data.errors) setErrors(data.errors);
        throw new Error('request_failed');
      }

      setIsSubmitting(false);
      setIsSuccess(true);

      setTimeout(() => {
        setIsSuccess(false);
        setFormData({
          company: '',
          name: '',
          email: '',
          phone: '',
          employees: '',
          industry: '',
          message: '',
          website: ''
        });
        if (onClose) onClose();
      }, 3000);
    } catch (err) {
      setIsSubmitting(false);
      setError('No pudimos enviar tu solicitud. Inténtalo de nuevo o escríbenos por WhatsApp.');
    }
  };

  if (isSuccess) {
    return (
      <Card className="max-w-md mx-auto">
        <CardContent className="text-center py-12">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">
            {isDemo ? '¡Demo Solicitada!' : '¡Mensaje Enviado!'}
          </h3>
          <p className="text-slate-600">
            {isDemo 
              ? 'Nuestro equipo se pondrá en contacto contigo pronto para programar tu demo personalizada.'
              : 'Gracias por contactarnos. Te responderemos en las próximas 24 horas.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-slate-900 flex items-center">
          {isDemo ? (
            <>
              <Building2 className="h-6 w-6 text-yellow-600 mr-2" />
              Solicitar Demo Empresarial
            </>
          ) : (
            <>
              <Mail className="h-6 w-6 text-yellow-600 mr-2" />
              Contacta con Nosotros
            </>
          )}
        </CardTitle>
        <p className="text-slate-600">
          {isDemo 
            ? 'Descubre cómo NOVOPAY puede transformar las finanzas de tu empresa con una demo personalizada.'
            : 'Estamos aquí para ayudarte. Completa el formulario y te contactaremos pronto.'}
        </p>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleSubmit} noValidate className="relative space-y-6">
          <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
            <label htmlFor="demo-website">Sitio web</label>
            <input id="demo-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={formData.website} onChange={handleInputChange} data-testid="demo-honeypot-website" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company">Empresa *</Label>
              <Input
                id="company"
                name="company"
                data-testid="demo-input-company"
                maxLength={200}
                value={formData.company}
                onChange={handleInputChange}
                placeholder="Nombre de tu empresa"
                required
                className={`focus:ring-yellow-500 focus:border-yellow-500 ${errCls('company')}`}
              />
              {errMsg('company')}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="name">Nombre Completo *</Label>
              <Input
                id="name"
                name="name"
                data-testid="demo-input-name"
                maxLength={200}
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Tu nombre completo"
                required
                className={`focus:ring-yellow-500 focus:border-yellow-500 ${errCls('name')}`}
              />
              {errMsg('name')}
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Correo Electrónico *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                data-testid="demo-input-email"
                maxLength={200}
                value={formData.email}
                onChange={handleInputChange}
                placeholder="tu@empresa.com"
                required
                className={`focus:ring-yellow-500 focus:border-yellow-500 ${errCls('email')}`}
              />
              {errMsg('email')}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Teléfono (10 dígitos)</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                inputMode="numeric"
                data-testid="demo-input-phone"
                maxLength={10}
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="5512345678"
                className={`focus:ring-yellow-500 focus:border-yellow-500 ${errCls('phone')}`}
              />
              {errMsg('phone')}
            </div>
          </div>
          
          {isDemo && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="employees">Número de Empleados</Label>
                <Select onValueChange={(value) => handleSelectChange('employees', value)}>
                  <SelectTrigger className="focus:ring-orange-500 focus:border-orange-500">
                    <SelectValue placeholder="Selecciona el tamaño" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-10">1-10 empleados</SelectItem>
                    <SelectItem value="11-50">11-50 empleados</SelectItem>
                    <SelectItem value="51-200">51-200 empleados</SelectItem>
                    <SelectItem value="201-1000">201-1000 empleados</SelectItem>
                    <SelectItem value="1000+">Más de 1000 empleados</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="industry">Industria</Label>
                <Select onValueChange={(value) => handleSelectChange('industry', value)}>
                  <SelectTrigger className="focus:ring-orange-500 focus:border-orange-500">
                    <SelectValue placeholder="Selecciona tu industria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="retail">Retail/Comercio</SelectItem>
                    <SelectItem value="manufacturing">Manufactura</SelectItem>
                    <SelectItem value="services">Servicios</SelectItem>
                    <SelectItem value="construction">Construcción</SelectItem>
                    <SelectItem value="technology">Tecnología</SelectItem>
                    <SelectItem value="healthcare">Salud</SelectItem>
                    <SelectItem value="education">Educación</SelectItem>
                    <SelectItem value="other">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="message">Mensaje {isDemo ? '(Opcional)' : '*'}</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder={isDemo 
                ? "Cuéntanos más sobre tus necesidades financieras específicas..."
                : "Describe cómo podemos ayudarte..."
              }
              required={!isDemo}
              maxLength={2000}
              data-testid="demo-input-message"
              className={`min-h-[100px] focus:ring-orange-500 focus:border-orange-500 ${errCls('message')}`}
            />
            {errMsg('message')}
          </div>

          {error && <p className="text-red-500 text-sm" data-testid="demo-form-error">{error}</p>}
          
          <Button
            type="submit"
            data-testid="demo-submit-btn"
            disabled={isSubmitting}
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-black py-3 font-semibold transition-all duration-200 transform hover:scale-105"
          >
            {isSubmitting ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Enviando...
              </div>
            ) : (
              <>
                {isDemo ? 'Solicitar Demo Gratuita' : 'Enviar Mensaje'}
                <Phone className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};